import ArrowDownSVG from 'assets/ver2/icons/arrow-down-yellow-icon.svg';
import TooltipCommon from 'components/common/TooltipCommon';
import TooltipCommonMobile from 'components/common/TooltipCommon/mobile';
import { DESKTOP_MIN_WIDTH } from 'constants';
import CalculatorRoundingAnimationLottie from 'features/calculatorRounding/components/CalculatorRoundingAnimationLottie';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { format } from 'helpers';
import { Fade } from 'helpers/animation';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import styled from 'styled-components';
import { fontSize12Mixin, fontSize14Mixin, paragraph500Mixin } from 'styles';

const CustomCalculatorFormAccordion = ({
  status,
  totalLabelReference,
  totalPayAmount,
  sendAmount,
  feeAmount,
  isShowDiscountPercent,
  discountPercent,
  originalFee,
  isShowSurchargeField,
  // Defaults true so screens that don't thread the flag keep current
  // behaviour; the amount node still requires isShowSurchargeField too.
  isShowSurchargeValue = true,
  surchargeAmount,
  isDiscountApplied,
  // Defaults true so screens that don't thread a settle signal keep current
  // behaviour; only the add-transaction calculator passes the real, live value
  // (false during apply/pending/fetch, including re-validation).
  isDiscountSettled = true,
  discountAmount,
  isExpandDefault = false,
  styleWrapper = {},
  styleWrapperBodyContent = {},
  styleAccordionButton = {},
  paymentMethod = null,
  discountApplied,
  isShowFeeDiscount,
  isShowFee,
  isForceCalculatorFormAccordionExpand,
}) => {
  const { t } = useLang();
  const { isMobile, isTablet } = useDetectDevice();

  const [isExpand, setExpand] = useState(isExpandDefault);

  const { type, code } = discountApplied || {};

  const { renderDiscountFieldLabel } = useAddTransaction();

  const { isCalculatorRounding } = useCalculatorRoundingStore();

  const isCalculatorRoundingBySendAmount = isCalculatorRounding && sendAmount;

  const handleAccordionItemOnClick = () => {
    if (isForceCalculatorFormAccordionExpand) return;
    setExpand(!isExpand);
  };

  const renderSendingAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>{`${format.toAmountFloatStr(
        sendAmount,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderFeeAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>{`${format.toAmountFloatStr(
        originalFee,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderDiscountAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue className="fee-value">
        -{`${format.toAmountFloatStr(originalFee - feeAmount)} AUD`}
      </CalculatorFieldValue>
    );
  };
  const renderDiscountSection = () => {
    if (!isShowFeeDiscount) return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel>
          {t('label_discounted_amount')}
          {isShowDiscountPercent && !isCalculatorRounding && (
            <CalculatorFieldValue className="summary-discount">
              {Boolean(discountPercent) && (
                <>
                  {isMobile || isTablet ? (
                    <TooltipCommonMobile
                      name="form-transaction-details-mobile"
                      text={t(discountPercent.tooltip)}
                      icon={discountPercent.imgSrc}
                      percentValue={discountPercent.percent}
                      isUseImage={false}
                    />
                  ) : (
                    <TooltipCommon
                      text={t(discountPercent.tooltip)}
                      icon={discountPercent.imgSrc}
                      percentValue={discountPercent.percent}
                      isPopover
                      isDangerouslySetInnerHTML={true}
                      isUseImage={false}
                    />
                  )}
                </>
              )}
            </CalculatorFieldValue>
          )}
        </CalculatorFieldLabel>
        {renderDiscountAmountValue()}
      </CalculatorFieldWrap>
    );
  };

  const renderDiscountApplied = () => {
    if (!isDiscountApplied || !isDiscountSettled || isCalculatorRounding)
      return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel
          dangerouslySetInnerHTML={{
            __html: renderDiscountFieldLabel(type, code),
          }}
          style={{
            display: 'grid',
            gap: 0,
            paddingBlock: '2px',
          }}
        />
        <CalculatorFieldValue style={{ whiteSpace: 'nowrap' }}>
          -{`${format.toAmountFloatStr(discountAmount)} AUD`}
        </CalculatorFieldValue>
      </CalculatorFieldWrap>
    );
  };

  const renderSurchargeAmountValue = () => {
    if (!isShowSurchargeValue) return null;
    if (isCalculatorRounding)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    const amount = Number(surchargeAmount);
    if (!Number.isFinite(amount)) return null;
    return (
      <CalculatorFieldValue>{`${format.toAmountFloatStr(
        amount,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderSurchargeField = () => {
    if (!isShowSurchargeField) return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel>
          {t('label_surcharge_label')} <span>{'(1.2%)'}</span>
        </CalculatorFieldLabel>
        {renderSurchargeAmountValue()}
      </CalculatorFieldWrap>
    );
  };

  const renderDiscountAppliedText = () => {
    if (!isDiscountApplied || !isDiscountSettled) return '';
    return (
      <DiscountAppliedText>{t('label_voucher_applied')}</DiscountAppliedText>
    );
  };
  const renderTotalToPayValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorTotalValue>
          <CalculatorRoundingAnimationLottie width="30px" height="30px" />
        </CalculatorTotalValue>
      );
    return (
      <CalculatorTotalValue>
        <TotalPayAmountAndDiscount>
          {`${format.toAmountFloatStr(totalPayAmount)} AUD`}{' '}
          {renderDiscountAppliedText()}
        </TotalPayAmountAndDiscount>
        {!isForceCalculatorFormAccordionExpand && (
          <ArrowIcon
            src={ArrowDownSVG}
            width={24}
            height={24}
            $isExpand={isExpand}
          />
        )}
      </CalculatorTotalValue>
    );
  };

  const renderCalculatorAccordionCollapseTitle = () => {
    if (!isForceCalculatorFormAccordionExpand) return <></>;
    return <Label>{t('label_transaction_details')}</Label>;
  };

  return (
    <Wrapper style={styleWrapper}>
      <Collapse in={isExpand} appear>
        <div id="calculator-accordion-collapse">
          <BodyContent $isExpand={isExpand} style={styleWrapperBodyContent}>
            {renderCalculatorAccordionCollapseTitle()}
            <CalculatorFieldWrap>
              <CalculatorFieldLabel>
                {t('label_sending_amount')}
              </CalculatorFieldLabel>
              {renderSendingAmountValue()}
            </CalculatorFieldWrap>
            {isShowFee && (
              <CalculatorFieldWrap>
                <CalculatorFieldLabel>{t('label_fee')}</CalculatorFieldLabel>
                {renderFeeAmountValue()}
              </CalculatorFieldWrap>
            )}
            {renderDiscountSection()}
            {renderDiscountApplied()}
            {renderSurchargeField()}
          </BodyContent>
        </div>
      </Collapse>
      <AccordionButton
        onClick={handleAccordionItemOnClick}
        aria-controls="calculator-accordion-collapse"
        aria-expanded={isExpand}
        $isExpand={isExpand}
        style={styleAccordionButton}
      >
        <CalculatorTotal>
          <CalculatorTotalLabel>
            {status && totalLabelReference
              ? totalLabelReference[status]?.()
              : paymentMethod
                ? t('label_total_to_pay')
                : t('label_sub_total')}
          </CalculatorTotalLabel>
          {renderTotalToPayValue()}
        </CalculatorTotal>
      </AccordionButton>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: grid;
  width: 100%;
  height: fit-content;
  margin-block: 0px;
  ${Fade};
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-block: 16px;
  }
`;

const AccordionButton = styled(Button)`
  padding: 0;
  padding-inline: 16px;
  background-color: var(--ds-c-blue-remox-hover) !important;
  box-shadow: none !important;
  display: block;
  border-radius: 0px;
  transition: 0.6s ease;
  width: 100%;
  height: 52px;
  border: none;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding-inline: 10px;
    border-radius: ${(props) =>
      props.$isExpand ? '0px 0px 8px 8px!important' : '8px!important'};
  }
`;
const ArrowIcon = styled.img`
  transform: ${(props) =>
    props.$isExpand ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: 0.3s ease;
`;

const BodyContent = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-blue);

  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: var(--ds-c-blue-remox-light);
  padding-inline: 16px;
  border-radius: 0px;
  transition: 0.6s ease;

  & p {
    cursor: text;
    margin: 0;
    padding: 0;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding-inline: 10px;
    border-radius: ${(props) => (props.$isExpand ? '8px 8px 0px 0px' : '0px')};
  }
`;

const CalculatorTotal = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  width: 100%;
  background: transparent;
  border-radius: 8px;
`;
const CalculatorTotalLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-white);
  text-transform: uppercase;

  margin: 0;
  padding: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const CalculatorTotalValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 29px;
  text-transform: uppercase;
  color: var(--ds-c-white);

  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
`;

const CalculatorFieldWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  height: fit-content;
  margin-bottom: 0px;
  border-bottom: 1px dashed #979797;

  &:last-child {
    border-bottom: none;
  }
`;
const CalculatorFieldLabel = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  font-weight: 400;

  display: flex;
  align-items: flex-end;
  gap: 4px;

  margin: 0;
  padding: 0;

  & span {
    &.discount-code {
      color: var(--ds-c-green-default);
    }
  }
`;
const CalculatorFieldValue = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  font-weight: 400;
  text-transform: uppercase;

  &.fee-value {
    display: flex;
    align-items: center;
    gap: 8px;

    & button {
      height: fit-content;
      background-color: transparent !important;
      padding-inline: 0px;
    }
  }

  &.summary-discount {
    display: flex;
    align-items: center;
    color: #b0b6bd;

    & button {
      padding-bottom: 0px;
    }
  }

  & span {
    font-size: 14px;
    line-height: 17px;
    color: #ffffff;

    background-color: var(--ds-c-green-default);
    padding: 2px 8px;
    border-radius: 2px;
    margin-right: 8px;
  }

  & b {
    font-weight: 500;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & span {
      font-size: 14px;
      line-height: 17px;
    }
  }
`;

const DiscountAppliedText = styled.p`
  ${paragraph500Mixin};
  ${fontSize12Mixin};
  color: #33f268;
  text-align: right;
  text-transform: none;
`;

const TotalPayAmountAndDiscount = styled.div`
  display: grid;
  text-align: right;
`;

const Label = styled.p`
  margin: 0;
  margin-bottom: 10px !important;
  padding: 0;

  font-family: var(--ff-primary);
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  text-align: left;
  color: var(--ds-c-grey-dark);
  text-transform: uppercase;
`;

export default CustomCalculatorFormAccordion;
